/**
 * Conversation Flow Service
 *
 * This service loads and manages the conversation flow configuration from JSON.
 * The JSON file path is configured via VITE_CONVERSATION_FLOW_CONFIG environment variable.
 * Any changes to the JSON file will be reflected in the chat.
 */

// Get the config file name from environment variable
const CONFIG_FILE = import.meta.env.VITE_CONVERSATION_FLOW_CONFIG || 'conversation-flow-v2.json';

// Dynamic import based on config file - Vite will handle the bundling
// We import all possible config files and select the right one at runtime
const configModules = import.meta.glob('../../docs/*.json', { eager: true });
const configPath = `../../docs/${CONFIG_FILE}`;
const conversationFlowData = configModules[configPath]?.default || configModules[Object.keys(configModules)[0]]?.default;

if (!conversationFlowData) {
  console.error(`Failed to load conversation flow config: ${CONFIG_FILE}`);
  console.error('Available configs:', Object.keys(configModules));
}

class ConversationFlowService {
  constructor() {
    this.flowData = conversationFlowData || {};
    this.cache = {};
    this.configFileName = CONFIG_FILE;
  }

  /**
   * Get the name of the loaded config file
   */
  getConfigFileName() {
    return this.configFileName;
  }

  /**
   * Get the full flow configuration
   */
  getFlowData() {
    return this.flowData;
  }

  /**
   * Get global rules from config
   */
  getGlobalRules() {
    return this.flowData.globalRules || {};
  }

  /**
   * Get the "more options" button configuration from global rules
   */
  getMoreOptionsConfig() {
    return this.flowData.globalRules?.moreOptionsHandling?.button || {
      text: "🔄 Give me more options",
      value: "more_options",
      action: "regenerate_options"
    };
  }

  /**
   * Get phase definitions
   */
  getPhases() {
    return this.flowData.phases;
  }

  /**
   * Get a specific phase by ID
   */
  getPhase(phaseId) {
    const phases = this.flowData.phases;
    if (!phases) return null;

    // Find by id field or by key
    for (const [key, phase] of Object.entries(phases)) {
      if (phase.id === phaseId || key === phaseId) {
        return { ...phase, key };
      }
    }
    return null;
  }

  /**
   * Get phase info (title, subtitle, emoji) for display
   */
  getPhaseInfo() {
    const phases = this.flowData.phases;
    if (!phases) return {};

    const info = {};
    for (const [key, phase] of Object.entries(phases)) {
      info[phase.id] = {
        title: phase.title,
        subtitle: phase.subtitle,
        emoji: phase.emoji,
        persona: phase.persona
      };
    }
    return info;
  }

  /**
   * Get the creative brief schema
   */
  getCreativeBriefSchema() {
    return this.flowData.creativeBriefSchema || {};
  }

  /**
   * Get initial empty creative brief based on schema
   */
  getEmptyCreativeBrief() {
    const schema = this.flowData.creativeBriefSchema || {};
    const brief = {};
    for (const [key, config] of Object.entries(schema)) {
      if (config.type === 'array') {
        brief[key] = [];
      } else if (config.type === 'object') {
        brief[key] = {};
      } else {
        brief[key] = '';
      }
    }
    return brief;
  }

  /**
   * Get conversation flow for a specific phase
   */
  getPhaseFlow(phaseId) {
    if (!this.flowData.conversationFlow) return null;
    // Convert snake_case to camelCase (e.g., creative_director -> creativeDirector)
    const camelCaseId = phaseId.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    const flowKey = `${camelCaseId}Phase`;
    return this.flowData.conversationFlow[flowKey] || null;
  }

  /**
   * Get a specific step from a phase
   * Uses stepFlows to determine the field, then finds matching step in conversationFlow
   */
  getPhaseStep(phaseId, stepIndex) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    if (!phaseFlow || !phaseFlow.steps) {
      console.error(`[getPhaseStep] No phase flow or steps for phase: ${phaseId}`);
      return null;
    }

    // First, try to get the field name from stepFlows
    const stepFlowConfig = this.flowData.stepSequencing?.stepFlows?.[phaseId]?.[stepIndex.toString()];
    console.log(`[getPhaseStep] Phase: ${phaseId}, stepIndex: ${stepIndex}, stepFlowConfig field: ${stepFlowConfig?.field || 'N/A'}`);

    if (stepFlowConfig?.field) {
      // Find the step with matching field in the steps array
      const matchingStep = phaseFlow.steps.find(step => step.field === stepFlowConfig.field);
      if (matchingStep) {
        console.log(`[getPhaseStep] Found step by field: ${stepFlowConfig.field}`);
        return matchingStep;
      }
      console.warn(`[getPhaseStep] No step with field "${stepFlowConfig.field}" found in ${phaseId}.steps (stepIndex: ${stepIndex})`);
      console.log(`[getPhaseStep] Available fields:`, phaseFlow.steps.map(s => s.field));
    }

    // Fallback to array index
    const stepByIndex = phaseFlow.steps[stepIndex];
    if (!stepByIndex) {
      console.warn(`[getPhaseStep] No step at index ${stepIndex} in ${phaseId}.steps (array length: ${phaseFlow.steps.length})`);
    } else {
      console.log(`[getPhaseStep] Found step by array index: ${stepByIndex.field}`);
    }
    return stepByIndex || null;
  }

  /**
   * Get step by field name
   */
  getStepByField(phaseId, fieldName) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    if (!phaseFlow || !phaseFlow.steps) return null;
    return phaseFlow.steps.find(step => step.field === fieldName) || null;
  }

  /**
   * Get required fields for a phase (derived from steps with requiredField: true)
   */
  getPhaseRequirements(phaseId) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    if (!phaseFlow || !phaseFlow.steps) return [];

    // Derive requirements from steps with requiredField: true
    return phaseFlow.steps
      .filter(step => step.requiredField === true)
      .map(step => step.field);
  }

  /**
   * Get a nested value from an object using dot notation (e.g., "videoSettings.videoLength")
   */
  getNestedValue(obj, path) {
    if (!path) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Set a nested value in an object using dot notation
   */
  setNestedValue(obj, path, value) {
    if (!path) return obj;
    const keys = path.split('.');
    const result = { ...obj };
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      current[key] = current[key] ? { ...current[key] } : {};
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return result;
  }

  /**
   * Check if a phase can transition to next
   */
  canTransitionPhase(phaseId, creativeBrief) {
    const requirements = this.getPhaseRequirements(phaseId);
    const missing = requirements.filter(req => {
      const value = this.getNestedValue(creativeBrief, req);
      return value === undefined || value === null || value === '';
    });
    return {
      canTransition: missing.length === 0,
      missingFields: missing
    };
  }

  /**
   * Get entry point message and options
   */
  getEntryPoint() {
    return this.flowData.conversationFlow?.entryPoint || {
      message: "Let's create something amazing! How would you like to start?",
      options: [
        { text: "Guide me through it", value: "guided" },
        { text: "I know what I want", value: "direct" }
      ]
    };
  }

  /**
   * Get AI agent prompts configuration
   */
  getAIPrompts() {
    return this.flowData.aiAgentPrompts || {};
  }

  /**
   * Get system prompt base
   */
  getSystemPromptBase() {
    return this.flowData.aiAgentPrompts?.systemPromptBase?.content || '';
  }

  /**
   * Get phase-specific AI prompt template
   */
  getPhasePromptTemplate(phaseId) {
    return this.flowData.aiAgentPrompts?.phasePrompts?.[phaseId]?.template || '';
  }

  /**
   * Get rabbit hole detection config
   */
  getRabbitHoleConfig() {
    return this.flowData.rabbitHoleDetection || {};
  }

  /**
   * Get brief review configuration
   */
  getBriefReviewConfig() {
    return this.flowData.conversationFlow?.briefReviewPhase || {};
  }

  /**
   * Replace template variables in a string
   */
  interpolate(template, variables) {
    if (!template) return '';
    let result = template;
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{${key}}`;
      result = result.split(placeholder).join(value || '');
    }
    return result;
  }

  /**
   * Get hardcoded options for a step (with variable interpolation)
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @param {object} variables - Variables for interpolation
   * @param {object} creativeBrief - The creative brief (needed for conditional options like platform selection)
   */
  getStepOptions(phaseId, stepIndex, variables = {}, creativeBrief = null) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    if (!step) return null;

    let options = null;

    // Check if this step has output-type-specific options (e.g., platform selection in format_selection)
    if (step.conditionalDisplay?.filterOptionsByOutputType && creativeBrief?.outputType) {
      const outputType = creativeBrief.outputType;
      if (outputType === 'image' && step.imageOptions) {
        options = step.imageOptions;
      } else if (outputType === 'video' && step.videoOptions) {
        options = step.videoOptions;
      } else if (outputType === 'carousel' && step.carouselOptions) {
        options = step.carouselOptions;
      }
    }

    // If options are hardcoded (standard options array)
    if (!options && step.options) {
      options = step.options;
    }

    // If options have a fallback
    if (!options && step.optionsGeneration?.fallback?.options) {
      options = step.optionsGeneration.fallback.options;
    }

    if (!options) return null;

    return options.map(opt => ({
      ...opt,
      text: this.interpolate(opt.text || opt.title, variables)
    }));
  }

  /**
   * Get the "always include" options for a step (custom input, help, etc.)
   * These are always appended to any options shown
   */
  getAlwaysIncludeOptions(phaseId, stepIndex) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    return step?.alwaysInclude || [];
  }

  /**
   * Check if a step uses AI-generated options
   */
  isAIGeneratedStep(phaseId, stepIndex) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    return step?.optionsGeneration?.source === 'AI_GENERATED';
  }

  /**
   * Get the prompt template for AI-generated options
   * Handles both single promptTemplate strings and dynamic generationPrompt objects
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @param {object} creativeBrief - The creative brief state (for dynamic prompt selection)
   * @returns {string} - The prompt template to use
   */
  getAIPromptTemplate(phaseId, stepIndex, creativeBrief = {}) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    if (!step?.optionsGeneration) return '';

    // If there's a direct promptTemplate, use it
    if (step.optionsGeneration.promptTemplate) {
      return step.optionsGeneration.promptTemplate;
    }

    // If there's a generationPrompt object (keyed by a field value), look up the right prompt
    if (step.optionsGeneration.generationPrompt && typeof step.optionsGeneration.generationPrompt === 'object') {
      const generationPrompt = step.optionsGeneration.generationPrompt;
      const basedOnFields = step.optionsGeneration.basedOn || [];

      // Try each basedOn field to find a matching key in generationPrompt
      for (const field of basedOnFields) {
        const fieldValue = this.getNestedValue(creativeBrief, field);
        if (fieldValue && generationPrompt[fieldValue]) {
          console.log(`[getAIPromptTemplate] Found dynamic prompt for ${field}=${fieldValue}`);
          return generationPrompt[fieldValue];
        }
      }

      // If no specific key matched, try to use a default key
      if (generationPrompt.default) {
        return generationPrompt.default;
      }

      // Return the first available prompt as fallback
      const firstKey = Object.keys(generationPrompt)[0];
      if (firstKey) {
        console.warn(`[getAIPromptTemplate] No matching key found, using first available: ${firstKey}`);
        return generationPrompt[firstKey];
      }
    }

    return '';
  }

  /**
   * Get fallback message for a step
   */
  getFallbackMessage(phaseId, stepIndex) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    return step?.optionsGeneration?.fallbackMessage ||
      step?.optionsGeneration?.fallback?.message ||
      step?.message ||
      step?.greeting?.message || '';
  }

  /**
   * Get step message (greeting or regular message)
   */
  getStepMessage(phaseId, stepIndex, variables = {}, creativeBrief = null) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    if (!step) return '';

    // Check multiple locations for the message (in priority order)
    let message = step.greeting?.message ||
      step.message ||
      step.optionsGeneration?.fallback?.message ||
      step.optionsGeneration?.fallbackMessage ||
      '';

    // Handle object messages with conditional logic
    if (typeof message === 'object' && message !== null) {
      // Check for specific conditions
      if (message.hasRetrievedImages && creativeBrief &&
        (creativeBrief.retrievedImages?.length > 0 || creativeBrief.scrapedImages?.length > 0)) {
        message = message.hasRetrievedImages;
      } else if (message.default) {
        message = message.default;
      } else {
        // If no known keys match, try to use the first string value
        // This handles cases where we might have other conditional keys but no logic for them yet
        const stringVal = Object.values(message).find(v => typeof v === 'string');
        message = stringVal || '';

        if (!stringVal) {
          console.warn(`[ConversationFlowService] Step message is an object but no suitable string found. Keys: ${Object.keys(message).join(', ')}`);
        }
      }
    }

    return this.interpolate(message, variables);
  }

  /**
   * Get transition message for a phase
   */
  getTransitionMessage(phaseId) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    return phaseFlow?.transitionMessage || '';
  }

  /**
   * Get conditional drill-down options (for art_director phase text overlays)
   */
  getConditionalDrillDown(phaseId, stepIndex, conditionValue) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    return step?.conditionalDrillDown?.[conditionValue] || null;
  }

  /**
   * Get all field keys from the creative brief schema
   */
  getAllBriefFields() {
    return Object.keys(this.flowData.creativeBriefSchema || {});
  }

  /**
   * Get the phase order for navigation
   * Uses the phaseOrder from JSON, with conditional filtering for video_director/still_director
   */
  getPhaseOrder(creativeBrief = null) {
    // Get the phase order from JSON config
    const configOrder = this.flowData.stepSequencing?.phaseOrder || [
      'strategist',
      'creative_director',
      'copywriter',
      'art_director',
      'brief_review',
      'format_selection',
      'video_director',
      'still_director',
      'generation'
    ];

    // Filter out conditional phases that don't apply
    return configOrder.filter(phaseId => {
      const conditionalPhase = this.flowData.stepSequencing?.conditionalPhases?.[phaseId];
      if (!conditionalPhase) {
        return true; // Not conditional, always include
      }

      // Evaluate condition - if no creativeBrief yet, include the phase for now
      if (!creativeBrief) {
        return true;
      }

      return this.evaluateCondition(conditionalPhase.condition, creativeBrief);
    });
  }

  /**
   * Get still director configuration (handles both static images and carousels)
   * Iris asks about composition, focal points, and visual storytelling
   */
  getStillDirectorConfig() {
    return this.flowData.conversationFlow?.stillDirectorPhase || {
      id: 'still_director',
      description: 'Interactive phase for composition and visual narrative',
      sharedSteps: ['aspectRatio', 'compositionStyle', 'focalPoint', 'variationCount'],
      carouselOnlySteps: ['carouselCardCount', 'hookStrategy', 'storyArcType', 'visualConsistency', 'ctaPlacement']
    };
  }

  /**
   * Check if current output type requires a specific director phase
   */
  getRequiredDirectorPhase(outputType) {
    switch (outputType) {
      case 'video':
        return 'video_director';
      case 'image':
      case 'carousel':
        return 'still_director';
      default:
        return null;
    }
  }

  /**
   * Get next phase in sequence
   * @param {string} currentPhaseId - Current phase ID
   * @param {object} creativeBrief - The creative brief (needed to determine if video_director should be included)
   */
  getNextPhase(currentPhaseId, creativeBrief = null) {
    const order = this.getPhaseOrder(creativeBrief);
    const currentIndex = order.indexOf(currentPhaseId);
    if (currentIndex === -1 || currentIndex >= order.length - 1) {
      return null;
    }
    return order[currentIndex + 1];
  }

  /**
   * Get previous phase in sequence
   * @param {string} currentPhaseId - Current phase ID
   * @param {object} creativeBrief - The creative brief (needed to determine if video_director should be included)
   */
  getPreviousPhase(currentPhaseId, creativeBrief = null) {
    const order = this.getPhaseOrder(creativeBrief);
    const currentIndex = order.indexOf(currentPhaseId);
    if (currentIndex <= 0) {
      return null;
    }
    return order[currentIndex - 1];
  }

  /**
   * Generate AI response for Mia's skip message when user chooses static image
   * @param {object} creativeBrief - The creative brief for context
   */
  async generateMiaSkipMessage(creativeBrief) {
    const skipConfig = this.flowData.conversationFlow?.videoDirectorPhase?.skipMessage;
    if (!skipConfig?.isAIGenerated) {
      return skipConfig?.message || "Static image it is! Catch you on the next one.";
    }
    // The actual AI generation should be handled by the conversation agent
    // This returns the config for it
    return {
      isAIGenerated: true,
      promptTemplate: skipConfig.promptTemplate,
      exampleResponses: skipConfig.exampleResponses
    };
  }

  /**
   * Get video director phase configuration
   */
  getVideoDirectorConfig() {
    return this.flowData.conversationFlow?.videoDirectorPhase || null;
  }

  /**
   * Check if output type selection step exists in format_selection phase
   */
  hasOutputTypeStep() {
    const formatSelectionPhase = this.getPhaseFlow('format_selection');
    return formatSelectionPhase?.steps?.some(step => step.field === 'outputType') || false;
  }

  // ============================================
  // STEP SEQUENCING METHODS (JSON-DRIVEN FLOW)
  // ============================================

  /**
   * Get the step sequencing configuration
   */
  getStepSequencing() {
    return this.flowData.stepSequencing || {};
  }

  /**
   * Get the total number of steps in a phase
   * Uses stepFlows from stepSequencing as the source of truth for step count
   * @param {string} phaseId - The phase ID
   * @returns {number} - Number of steps in the phase
   */
  getPhaseStepCount(phaseId) {
    // Primary: count steps defined in stepFlows (most reliable - these define the actual navigation)
    const stepFlows = this.flowData.stepSequencing?.stepFlows?.[phaseId];
    if (stepFlows) {
      const stepIndices = Object.keys(stepFlows).map(k => parseInt(k, 10)).filter(n => !isNaN(n));
      if (stepIndices.length > 0) {
        // Step count is max index + 1 (since steps are 0-indexed)
        return Math.max(...stepIndices) + 1;
      }
    }

    // Secondary: check phaseStepCounts from stepSequencing
    const stepCounts = this.flowData.stepSequencing?.phaseStepCounts;
    if (stepCounts && stepCounts[phaseId] !== undefined) {
      return stepCounts[phaseId];
    }

    // Tertiary: count steps in the phase flow array
    const phaseFlow = this.getPhaseFlow(phaseId);
    return phaseFlow?.steps?.length || 0;
  }

  /**
   * Check if a step should be skipped based on conditions
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @param {object} creativeBrief - The current creative brief state
   * @returns {object} - { shouldSkip: boolean, skipTo: number | null }
   */
  shouldSkipStep(phaseId, stepIndex, creativeBrief) {
    const conditionalSteps = this.flowData.stepSequencing?.conditionalSteps?.[phaseId];
    if (!conditionalSteps) {
      return { shouldSkip: false, skipTo: null };
    }

    const stepConfig = conditionalSteps[stepIndex.toString()];
    if (!stepConfig) {
      return { shouldSkip: false, skipTo: null };
    }

    // Evaluate the condition
    const conditionMet = this.evaluateCondition(stepConfig.condition, creativeBrief);

    // If condition is NOT met, skip this step
    if (!conditionMet) {
      return { shouldSkip: true, skipTo: stepConfig.skipTo };
    }

    return { shouldSkip: false, skipTo: null };
  }

  /**
   * Evaluate a condition string against the creative brief
   * @param {string} condition - Condition string like "outputType === 'video'"
   * @param {object} creativeBrief - The creative brief state
   * @returns {boolean} - Whether the condition is met
   */
  evaluateCondition(condition, creativeBrief) {
    if (!condition) return true;

    try {
      // Handle nested properties like videoSettings.videoType
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };

      // Helper function to evaluate a single condition (no OR/AND)
      const evaluateSingleCondition = (cond) => {
        cond = cond.trim();

        // Pattern: "field === 'value'" or "field !== 'value'"
        const equalMatch = cond.match(/^([\w.]+)\s*===\s*['"]?([^'"]+)['"]?$/);
        if (equalMatch) {
          const [, field, expectedValue] = equalMatch;
          const actualValue = getNestedValue(creativeBrief, field);
          return String(actualValue) === expectedValue;
        }

        const notEqualMatch = cond.match(/^([\w.]+)\s*!==\s*['"]?([^'"]+)['"]?$/);
        if (notEqualMatch) {
          const [, field, expectedValue] = notEqualMatch;
          const actualValue = getNestedValue(creativeBrief, field);
          return String(actualValue) !== expectedValue;
        }

        // Pattern: "field === true" or "field === false"
        const boolMatch = cond.match(/^([\w.]+)\s*===\s*(true|false)$/);
        if (boolMatch) {
          const [, field, expectedBool] = boolMatch;
          const actualValue = getNestedValue(creativeBrief, field);
          const result = actualValue === (expectedBool === 'true');
          console.log(`[evaluateCondition] Boolean check: ${field} = ${actualValue} (${typeof actualValue}), expected: ${expectedBool}, result: ${result}`);
          return result;
        }

        // Pattern: "field.selected === true" (for complex objects)
        if (cond.includes('.selected')) {
          const field = cond.split('.selected')[0];
          const actualValue = getNestedValue(creativeBrief, field);
          return actualValue?.selected === true;
        }

        console.warn(`Could not parse single condition: ${cond}`);
        return true; // Default to true if we can't parse
      };

      // Handle OR conditions (||)
      if (condition.includes('||')) {
        const orParts = condition.split('||');
        return orParts.some(part => evaluateSingleCondition(part));
      }

      // Handle AND conditions (&&)
      if (condition.includes('&&')) {
        const andParts = condition.split('&&');
        return andParts.every(part => evaluateSingleCondition(part));
      }

      // Single condition
      return evaluateSingleCondition(condition);
    } catch (error) {
      console.error('Error evaluating condition:', condition, error);
      return true;
    }
  }

  /**
   * Get the next step in the current phase
   * Uses stepFlows.nextStep values for explicit routing, with conditional step handling
   * @param {string} phaseId - The phase ID
   * @param {number} currentStep - Current step index
   * @param {object} creativeBrief - The creative brief state
   * @returns {object} - { nextStep: number | null, isPhaseComplete: boolean, nextPhase: string | null }
   */
  getNextStep(phaseId, currentStep, creativeBrief) {
    const totalSteps = this.getPhaseStepCount(phaseId);
    console.log(`[getNextStep] Phase: ${phaseId}, currentStep: ${currentStep}, totalSteps: ${totalSteps}`);

    // First, check stepFlows for explicit next step routing
    const stepFlowConfig = this.getStepFlowConfig(phaseId, currentStep);
    const onCompleteHandler = this.getStepOnCompleteHandler(phaseId, currentStep, creativeBrief);

    // Determine the raw next step from stepFlows (handles conditional routing)
    let rawNextStep;
    if (onCompleteHandler?.nextStep !== undefined) {
      rawNextStep = onCompleteHandler.nextStep;
      console.log(`[getNextStep] Using onCompleteHandler.nextStep: ${rawNextStep}`);
    } else if (stepFlowConfig?.nextStep !== undefined) {
      rawNextStep = stepFlowConfig.nextStep;
      console.log(`[getNextStep] Using stepFlowConfig.nextStep: ${rawNextStep}`);
    } else {
      // Default: increment by 1
      rawNextStep = currentStep + 1;
      console.log(`[getNextStep] Using default increment: ${rawNextStep}`);
    }

    // Check if stepFlows explicitly says phase is complete
    if (rawNextStep === 'phase_complete') {
      const nextPhase = this.getNextPhaseFromSequencing(phaseId, creativeBrief);
      console.log(`[getNextStep] Phase complete, nextPhase: ${nextPhase}`);
      return {
        nextStep: null,
        isPhaseComplete: true,
        nextPhase
      };
    }

    let nextStep = typeof rawNextStep === 'number' ? rawNextStep : currentStep + 1;
    console.log(`[getNextStep] Initial nextStep: ${nextStep}`);

    // Check for conditional skips on the calculated next step
    // Use a counter to prevent infinite loops
    let loopCount = 0;
    const maxLoops = totalSteps + 2; // Safety limit

    while (nextStep < totalSteps && loopCount < maxLoops) {
      loopCount++;
      const { shouldSkip, skipTo } = this.shouldSkipStep(phaseId, nextStep, creativeBrief);
      console.log(`[getNextStep] Checking step ${nextStep}: shouldSkip=${shouldSkip}, skipTo=${skipTo}`);
      if (shouldSkip) {
        // If skipTo is specified, use it; otherwise increment
        if (skipTo !== null && skipTo !== undefined) {
          // Check if skipTo would be "phase_complete" or beyond total steps
          if (skipTo === 'phase_complete' || skipTo >= totalSteps) {
            const nextPhase = this.getNextPhaseFromSequencing(phaseId, creativeBrief);
            console.log(`[getNextStep] Skip leads to phase complete, nextPhase: ${nextPhase}`);
            return {
              nextStep: null,
              isPhaseComplete: true,
              nextPhase
            };
          }
          nextStep = skipTo;
        } else {
          nextStep = nextStep + 1;
        }
      } else {
        break;
      }
    }

    // Check if we've completed the phase
    if (nextStep >= totalSteps) {
      const nextPhase = this.getNextPhaseFromSequencing(phaseId, creativeBrief);
      console.log(`[getNextStep] Step ${nextStep} >= totalSteps ${totalSteps}, phase complete, nextPhase: ${nextPhase}`);
      return {
        nextStep: null,
        isPhaseComplete: true,
        nextPhase
      };
    }

    console.log(`[getNextStep] Final result: nextStep=${nextStep}, isPhaseComplete=false`);
    return {
      nextStep,
      isPhaseComplete: false,
      nextPhase: null
    };
  }

  /**
   * Get the next phase based on sequencing rules and special transitions
   * @param {string} currentPhaseId - Current phase ID
   * @param {object} creativeBrief - The creative brief state
   * @returns {string | null} - Next phase ID or null
   */
  getNextPhaseFromSequencing(currentPhaseId, creativeBrief) {
    const sequencing = this.flowData.stepSequencing;

    // Check for special transitions first
    const specialTransitions = sequencing?.specialTransitions;
    if (specialTransitions) {
      for (const [key, transition] of Object.entries(specialTransitions)) {
        if (transition.from.phase === currentPhaseId && transition.from.afterCompletion) {
          const conditionMet = this.evaluateCondition(transition.condition, creativeBrief);
          if (conditionMet) {
            return transition.to;
          }
        }
      }
    }

    // Check if next phase should be skipped (conditional phases)
    const phaseOrder = sequencing?.phaseOrder || this.getPhaseOrder(creativeBrief);
    const currentIndex = phaseOrder.indexOf(currentPhaseId);

    if (currentIndex === -1 || currentIndex >= phaseOrder.length - 1) {
      return null;
    }

    let nextPhaseIndex = currentIndex + 1;
    while (nextPhaseIndex < phaseOrder.length) {
      const nextPhaseId = phaseOrder[nextPhaseIndex];
      const conditionalPhase = sequencing?.conditionalPhases?.[nextPhaseId];

      if (conditionalPhase) {
        const conditionMet = this.evaluateCondition(conditionalPhase.condition, creativeBrief);
        if (!conditionMet) {
          // Skip this phase
          nextPhaseIndex++;
          continue;
        }
      }

      return nextPhaseId;
    }

    return null;
  }

  /**
   * Check if a phase should be skipped entirely
   * @param {string} phaseId - The phase ID
   * @param {object} creativeBrief - The creative brief state
   * @returns {object} - { shouldSkip: boolean, reason: string | null }
   */
  shouldSkipPhase(phaseId, creativeBrief) {
    const conditionalPhases = this.flowData.stepSequencing?.conditionalPhases;
    if (!conditionalPhases || !conditionalPhases[phaseId]) {
      return { shouldSkip: false, reason: null };
    }

    const phaseConfig = conditionalPhases[phaseId];
    const conditionMet = this.evaluateCondition(phaseConfig.condition, creativeBrief);

    if (!conditionMet) {
      return {
        shouldSkip: true,
        reason: phaseConfig.skipMessage || null,
        autoTransition: phaseConfig.autoTransition || false,
        noUserQuestions: phaseConfig.noUserQuestions || false
      };
    }

    return { shouldSkip: false, reason: null };
  }

  /**
   * Get the field that should be collected at a specific step
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @returns {string | null} - The field name or null
   */
  getStepField(phaseId, stepIndex) {
    const step = this.getPhaseStep(phaseId, stepIndex);
    return step?.field || null;
  }

  /**
   * Find the step index for a specific field in a phase
   * @param {string} phaseId - The phase ID
   * @param {string} fieldName - The field name to find
   * @returns {number} - The step index or -1 if not found
   */
  findStepByField(phaseId, fieldName) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    if (!phaseFlow?.steps) return -1;

    return phaseFlow.steps.findIndex(step => step.field === fieldName);
  }

  /**
   * Get navigation info for the current position in the flow
   * @param {string} phaseId - Current phase ID
   * @param {number} stepIndex - Current step index
   * @param {object} creativeBrief - The creative brief state
   * @returns {object} - Complete navigation context
   */
  getNavigationContext(phaseId, stepIndex, creativeBrief) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    const currentStep = this.getPhaseStep(phaseId, stepIndex);
    const totalSteps = this.getPhaseStepCount(phaseId);
    const { nextStep, isPhaseComplete, nextPhase } = this.getNextStep(phaseId, stepIndex, creativeBrief);

    return {
      currentPhase: phaseId,
      currentStep: stepIndex,
      currentField: currentStep?.field || null,
      totalStepsInPhase: totalSteps,
      nextStep,
      isPhaseComplete,
      nextPhase,
      phaseTransitionMessage: isPhaseComplete ? this.getTransitionMessage(phaseId) : null,
      stepConfig: currentStep
    };
  }

  /**
   * Get all steps that should be displayed for a phase (excluding skipped ones)
   * @param {string} phaseId - The phase ID
   * @param {object} creativeBrief - The creative brief state
   * @returns {Array} - Array of step configs that apply
   */
  getActiveStepsForPhase(phaseId, creativeBrief) {
    const phaseFlow = this.getPhaseFlow(phaseId);
    if (!phaseFlow?.steps) return [];

    return phaseFlow.steps.filter((step, index) => {
      const { shouldSkip } = this.shouldSkipStep(phaseId, index, creativeBrief);
      return !shouldSkip;
    });
  }

  // ============================================
  // STEP FLOW HANDLER METHODS
  // ============================================

  /**
   * Get the step flow configuration for a specific step
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @returns {object | null} - The step flow config or null
   */
  getStepFlowConfig(phaseId, stepIndex) {
    const stepFlows = this.flowData.stepSequencing?.stepFlows?.[phaseId];
    if (!stepFlows) return null;
    return stepFlows[stepIndex.toString()] || null;
  }

  /**
   * Get the onComplete handler configuration for a step
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @param {object} creativeBrief - The creative brief state (for evaluating conditions)
   * @returns {object} - The handler config with resolved conditions
   */
  getStepOnCompleteHandler(phaseId, stepIndex, creativeBrief) {
    const stepFlow = this.getStepFlowConfig(phaseId, stepIndex);
    if (!stepFlow?.onComplete) {
      return null;
    }

    const onComplete = stepFlow.onComplete;

    // If there are conditional handlers, evaluate them
    if (onComplete.conditions && Array.isArray(onComplete.conditions)) {
      let elseHandler = null;

      console.log(`[getStepOnCompleteHandler] Phase: ${phaseId}, Step: ${stepIndex}, evaluating ${onComplete.conditions.length} conditions`);

      // First pass: check all 'if' conditions
      for (const condition of onComplete.conditions) {
        if (condition.else) {
          // Save the else/default case for later
          elseHandler = {
            handler: condition.handler,
            targetField: condition.targetField,
            nextStep: condition.nextStep,
            promptKey: condition.promptKey,
            waitForBackground: condition.waitForBackground,
            message: condition.message
          };
          console.log(`[getStepOnCompleteHandler] Saved else handler with nextStep: ${condition.nextStep}`);
          continue;
        }

        if (condition.if) {
          const conditionMet = this.evaluateCondition(condition.if, creativeBrief);
          console.log(`[getStepOnCompleteHandler] Condition "${condition.if}" => ${conditionMet}`);
          if (conditionMet) {
            console.log(`[getStepOnCompleteHandler] Returning 'if' handler with nextStep: ${condition.nextStep}`);
            return {
              handler: condition.handler,
              targetField: condition.targetField,
              nextStep: condition.nextStep,
              promptKey: condition.promptKey,
              waitForBackground: condition.waitForBackground,
              message: condition.message
            };
          }
        }
      }

      // No 'if' conditions matched, return the else handler if found
      if (elseHandler) {
        console.log(`[getStepOnCompleteHandler] No 'if' matched, returning else handler with nextStep: ${elseHandler.nextStep}`);
        return elseHandler;
      }
    }

    // Return the direct handler config
    return {
      handler: onComplete.handler,
      targetField: onComplete.targetField,
      promptKey: onComplete.promptKey,
      fallbackMessage: onComplete.fallbackMessage,
      dynamicOptions: onComplete.dynamicOptions,
      nextStep: stepFlow.nextStep,
      waitForBackground: onComplete.waitForBackground,
      message: onComplete.message
    };
  }

  /**
   * Get the next step from stepFlows config, considering conditions
   * @param {string} phaseId - The phase ID
   * @param {number} currentStep - The current step index
   * @param {object} creativeBrief - The creative brief state
   * @returns {number | string | null} - Next step index, 'phase_complete', or null
   */
  getNextStepFromFlows(phaseId, currentStep, creativeBrief) {
    const handler = this.getStepOnCompleteHandler(phaseId, currentStep, creativeBrief);

    if (handler?.nextStep !== undefined) {
      return handler.nextStep;
    }

    // Fall back to the base nextStep in the step flow config
    const stepFlow = this.getStepFlowConfig(phaseId, currentStep);
    return stepFlow?.nextStep || null;
  }

  /**
   * Get complete step execution context
   * This combines step config, flow config, and handler info
   * @param {string} phaseId - The phase ID
   * @param {number} stepIndex - The step index
   * @param {object} creativeBrief - The creative brief state
   * @returns {object} - Complete execution context
   */
  getStepExecutionContext(phaseId, stepIndex, creativeBrief) {
    const stepConfig = this.getPhaseStep(phaseId, stepIndex);
    const flowConfig = this.getStepFlowConfig(phaseId, stepIndex);
    const handler = this.getStepOnCompleteHandler(phaseId, stepIndex, creativeBrief);
    const { shouldSkip, skipTo } = this.shouldSkipStep(phaseId, stepIndex, creativeBrief);

    return {
      // Step definition from conversationFlow
      step: stepConfig,
      field: stepConfig?.field || flowConfig?.field,

      // Flow definition from stepSequencing.stepFlows
      flow: flowConfig,

      // Handler for when step completes
      onComplete: handler,

      // Skip info
      shouldSkip,
      skipTo,

      // Message to display
      message: this.getStepMessage(phaseId, stepIndex, {}, creativeBrief),
      options: this.getStepOptions(phaseId, stepIndex, {}, creativeBrief),
      alwaysInclude: this.getAlwaysIncludeOptions(phaseId, stepIndex),

      // AI generation info
      isAIGenerated: this.isAIGeneratedStep(phaseId, stepIndex),
      promptTemplate: this.getAIPromptTemplate(phaseId, stepIndex, creativeBrief)
    };
  }

  /**
   * Get all handler types used in the flow
   * Useful for the admin page to show available handlers
   */
  getAvailableHandlers() {
    return this.flowData.stepSequencing?._documentation?.handlers || {
      AI_GENERATE_OPTIONS: 'Generate options using AI based on context',
      SHOW_HARDCODED_OPTIONS: 'Display predefined options from JSON',
      SHOW_COLOR_SWATCHES: 'Display color picker interface',
      TRIGGER_UPLOAD: 'Open file upload dialog',
      SHOW_CONFIRMATION: 'Show confirmation flow (e.g., audience confirmation)',
      TRANSITION_PHASE: 'Move to next phase',
      SHOW_PLATFORM_OPTIONS: 'Show platform options based on output type',
      CHECK_LOGO: 'Check if brand has logo and show appropriate options',
      SHOW_ASSET_OPTIONS: 'Show asset upload/selection options',
      SHOW_CONDITIONAL_DRILLDOWN: 'Show drill-down options based on previous selection',
      AI_GENERATE_SCRIPT: 'Generate video script using AI',
      GENERATE_FIRST_FRAME_OPTIONS: 'Generate first frame image options',
      GENERATE_LAST_FRAME_OPTIONS: 'Generate last frame image options'
    };
  }

  /**
   * Validate step flow configuration
   * @param {string} phaseId - The phase ID
   * @returns {object} - Validation result with errors and warnings
   */
  validatePhaseFlow(phaseId) {
    const errors = [];
    const warnings = [];

    const phaseFlow = this.getPhaseFlow(phaseId);
    const stepFlows = this.flowData.stepSequencing?.stepFlows?.[phaseId];

    if (!phaseFlow?.steps) {
      errors.push(`No steps defined in conversationFlow for phase: ${phaseId}`);
      return { valid: false, errors, warnings };
    }

    if (!stepFlows) {
      warnings.push(`No stepFlows defined for phase: ${phaseId}. Using default sequential flow.`);
    }

    // Check each step has matching flow config
    phaseFlow.steps.forEach((step, index) => {
      const flowConfig = stepFlows?.[index.toString()];

      if (!flowConfig) {
        warnings.push(`Step ${index} (${step.field}) has no flow config`);
      } else {
        // Check field matches
        if (flowConfig.field && flowConfig.field !== step.field) {
          errors.push(`Step ${index} field mismatch: ${step.field} vs ${flowConfig.field}`);
        }

        // Check handler is valid
        if (flowConfig.onComplete?.handler) {
          const validHandlers = Object.keys(this.getAvailableHandlers());
          if (!validHandlers.includes(flowConfig.onComplete.handler)) {
            warnings.push(`Step ${index} uses unknown handler: ${flowConfig.onComplete.handler}`);
          }
        }
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  // ============================================
  // CONVERSATIONAL PHILOSOPHY METHODS
  // ============================================

  /**
   * Get the conversational philosophy configuration
   */
  getConversationalPhilosophy() {
    return this.flowData.conversationalPhilosophy || {};
  }

  /**
   * Get expert personality for a phase
   */
  getExpertPersonality(phaseId) {
    const phase = this.getPhase(phaseId);
    if (!phase?.persona) return null;

    const expertKey = `${phase.persona.name?.toLowerCase()}_${phase.persona.role?.toLowerCase().replace(' ', '_')}`;
    return this.flowData.conversationalPhilosophy?.expertPersonalities?.[expertKey] || phase.persona;
  }

  /**
   * Get URL handling configuration
   */
  getUrlHandlingConfig() {
    return this.flowData._documentation?.urlHandling || {};
  }

  /**
   * Get audience confirmation flow configuration
   */
  getAudienceConfirmationConfig() {
    const targetAudienceStep = this.getStepByField('strategist', 'targetAudience');
    return targetAudienceStep?.audienceConfirmationFlows || {};
  }
}

// Export singleton instance
const conversationFlowService = new ConversationFlowService();
export default conversationFlowService;

// Also export the class for testing
export { ConversationFlowService };
